import fetch from 'node-fetch';
import { useEffect } from 'react';
import { TrackingEvent } from '../../../common/AnalyticsContext/TrackingEvent';
import { useAnalytics } from '../../../common/AnalyticsContext/useAnalytics';
import { useUserData } from '../../../common/UserProvider/UserProvider';
import { useBrowser } from '../../../common/services/BrowserService';
import { env } from '../../../common/util/env';
import { GoogleOneTapResponse, accounts, PromptMomentNotification } from './GoogleConfigs';
import { OnTapLoginContainer } from './OneTapLogin.styles';
import { useScript } from './useScripts';

const googleSignInScriptURL: string = 'https://accounts.google.com/gsi/client';
const { MEGATRON_HOST, GOOGLE_ONETAP_KEY } = env;

export const OneTapLogin = (containerClassName?: string) => {
  const userData = useUserData();

  const script = useScript({ src: googleSignInScriptURL });
  const { window } = useBrowser();
  const refetchUser = useUserData().refetch;
  const { trackEvent } = useAnalytics();
  const gooogleKey = `${GOOGLE_ONETAP_KEY}`;

  useEffect(() => {
    const onOneTapSignedIn = (response: GoogleOneTapResponse) => {
      trackEvent(TrackingEvent.GOOGLE_ONE_TAP_LOGGEDIN);

      const url = `${MEGATRON_HOST}/members/login/google-onetap/code`;

      const options = {
        async: true,
        method: 'POST',
        crossDomain: false,
        processData: false,
        body: JSON.stringify({ code: response.credential }),
      };

      fetch(url, options)
        .then(() => {
          refetchUser?.();
        })
        .catch(error => {
          console.error('There was an error!', error);
        });
    };

    const initializeGSI = (accounts?: accounts) => {
      if (accounts) {
        accounts.id.initialize({
          // eslint-disable-next-line camelcase
          client_id: gooogleKey,
          // eslint-disable-next-line camelcase
          cancel_on_tap_outside: true,
          callback: onOneTapSignedIn,
          // eslint-disable-next-line camelcase
          prompt_parent_id: containerClassName,
        });

        accounts.id.prompt((notification: PromptMomentNotification) => {
          if (notification.isNotDisplayed()) {
            trackEvent(TrackingEvent.GOOGLE_ONE_TAP_NOT_DISPLAYED, {
              errorData: {
                errorMessage: 'google one tap',
                errorReason: notification.getNotDisplayedReason(),
              },
            });
          } else if (notification.isSkippedMoment()) {
            trackEvent(TrackingEvent.GOOGLE_ONE_TAP_SKIPPED, {
              errorData: {
                errorMessage: 'google one tap',
                errorReason: notification.getSkippedReason(),
              },
            });
          } else if (notification.isDismissedMoment()) {
            trackEvent(TrackingEvent.GOOGLE_ONE_TAP_DISMISSED, {
              errorData: {
                errorMessage: 'google one tap',
                errorReason: notification.getDismissedReason(),
              },
            });
          }
        });
      }
    };

    if (script === 'ready' && !userData.loading && !userData.data) {
      initializeGSI(window?.google?.accounts);
    }
  }, [
    containerClassName,
    gooogleKey,
    refetchUser,
    script,
    trackEvent,
    userData.data,
    userData.loading,
    window?.google?.accounts,
  ]);
};

export const OneTapLoginWrapped = ({ containerId = 'one-tap-login-container' }) => {
  OneTapLogin(containerId);

  return <OnTapLoginContainer id={containerId} />;
};
